/**
 * Conversation Persistence Service
 *
 * Handles saving/loading conversations to/from disk via the Python backend.
 * Uses obfuscation to protect sensitive system prompts in stored files.
 *
 * Architecture:
 * - TypeScript manages the in-memory conversation state
 * - Python backend handles file I/O (TypeScript runs in Office.js browser context)
 * - Conversations are stored as obfuscated JSON files in CarouselData
 * - An index.json file maintains metadata for quick listing
 *
 * Storage Location:
 * CarouselData/carousel_ppt_ondevice_db/conversations/
 * ├── index.json                    # Metadata index (not obfuscated)
 * ├── pconv_abc123def456.json       # Full conversation (obfuscated)
 * └── ...
 */
let BACKEND_BASE_URL = "";
try {
    BACKEND_BASE_URL = require("@carousel-office/shared").PYTHON_BACKEND_URL || "";
}
catch { }
if (!BACKEND_BASE_URL) {
    console.warn("[ConversationPersistence] PYTHON_BACKEND_URL is not configured.");
}
let authService;
try {
    authService = require('@carousel-office/shared').authService;
}
catch { }
import { obfuscateJson, deobfuscateJson } from './obfuscation';
import { PERSISTENCE_SCHEMA_VERSION, } from './types';
// ═══════════════════════════════════════════════════════════════════════════
// CONVERSATION PERSISTENCE CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Service for persisting conversations to disk
 *
 * @example
 * const persistence = ConversationPersistence.getInstance();
 *
 * // Save a conversation
 * await persistence.saveConversation({
 *   conversationId: 'builder_plan_abc123',
 *   origin: 'builder',
 *   title: 'Q4 Earnings Presentation',
 *   documentId: 'deck_xyz789',
 *   // ... full conversation data
 * });
 *
 * // List conversations
 * const convos = await persistence.listConversations({ origin: 'builder' });
 *
 * // Load a conversation
 * const loaded = await persistence.loadConversation('builder_plan_abc123');
 */
export class ConversationPersistence {
    constructor() {
        /** Cache of conversation metadata for quick access */
        this.indexCache = null;
        /** Whether the cache is dirty and needs to be refreshed */
        this.indexCacheDirty = true;
    }
    /**
     * Get singleton instance
     */
    static getInstance() {
        if (!ConversationPersistence.instance) {
            ConversationPersistence.instance = new ConversationPersistence();
        }
        return ConversationPersistence.instance;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // SAVE OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Save a conversation to disk
     *
     * @param conversation - The full conversation to persist
     * @returns Promise that resolves when saved
     */
    async saveConversation(conversation) {
        console.log(`[ConversationPersistence] Saving conversation: ${conversation.conversationId}`);
        try {
            // 1. Prepare the file content with obfuscation
            const fileContent = {
                _version: PERSISTENCE_SCHEMA_VERSION,
                _obfuscated: true,
                data: obfuscateJson(conversation),
            };
            // 2. Save via backend API
            await this.saveToBackend(conversation.conversationId, fileContent);
            // 3. Update the index
            await this.updateIndex(this.extractMetadata(conversation));
            // 4. Mark cache as dirty
            this.indexCacheDirty = true;
            console.log(`[ConversationPersistence] Saved conversation: ${conversation.conversationId}`);
        }
        catch (error) {
            console.error(`[ConversationPersistence] Failed to save conversation:`, error);
            throw error;
        }
    }
    /**
     * Save multiple conversations in batch
     */
    async saveConversations(conversations) {
        console.log(`[ConversationPersistence] Batch saving ${conversations.length} conversations`);
        // Save each conversation
        for (const conv of conversations) {
            await this.saveConversation(conv);
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // LOAD OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Load a conversation from disk
     *
     * @param conversationId - The conversation ID to load
     * @returns The loaded conversation, or null if not found
     */
    async loadConversation(conversationId) {
        console.log(`[ConversationPersistence] Loading conversation: ${conversationId}`);
        try {
            const fileContent = await this.loadFromBackend(conversationId);
            if (!fileContent) {
                console.log(`[ConversationPersistence] Conversation not found: ${conversationId}`);
                return null;
            }
            // Deobfuscate if needed
            let conversation = null;
            if (fileContent._obfuscated && typeof fileContent.data === 'string') {
                conversation = deobfuscateJson(fileContent.data);
            }
            else if (typeof fileContent.data === 'object') {
                conversation = fileContent.data;
            }
            if (conversation) {
                console.log(`[ConversationPersistence] Loaded conversation: ${conversationId}`);
                return conversation;
            }
            console.warn(`[ConversationPersistence] Invalid file format for: ${conversationId}`);
            return null;
        }
        catch (error) {
            console.error(`[ConversationPersistence] Failed to load conversation:`, error);
            return null;
        }
    }
    /**
     * Load a conversation and convert to in-memory format
     * Returns data structures ready for ThreadManager and ContextResolver
     */
    async loadConversationForRestore(conversationId) {
        const conversation = await this.loadConversation(conversationId);
        if (!conversation) {
            return null;
        }
        // Convert threads to Map<ThreadId, ThreadState>
        const threads = new Map();
        for (const [threadId, persistedThread] of Object.entries(conversation.threads)) {
            threads.set(threadId, {
                threadId: persistedThread.threadId,
                agentType: persistedThread.agentType,
                conversationId: persistedThread.conversationId,
                documentId: persistedThread.documentId,
                messages: persistedThread.messages,
                createdAt: persistedThread.createdAt,
                lastActivityAt: persistedThread.lastActivityAt,
                tokenUsage: persistedThread.tokenUsage,
            });
        }
        // Convert context entries to Map
        const contextEntries = new Map();
        for (const [contextId, entry] of Object.entries(conversation.contextCache || {})) {
            contextEntries.set(contextId, {
                content: entry.content,
                storedAt: entry.storedAt,
            });
        }
        // Convert image entries to Map
        const imageEntries = new Map();
        for (const [imageId, entry] of Object.entries(conversation.imageCache || {})) {
            imageEntries.set(imageId, {
                base64: entry.base64,
                mimeType: entry.mimeType,
                metadata: entry.metadata,
            });
        }
        return {
            metadata: this.extractMetadata(conversation),
            threads,
            contextEntries,
            imageEntries,
            buildProgress: conversation.buildProgress,
            searchHistory: conversation.searchHistory,
        };
    }
    // ═══════════════════════════════════════════════════════════════════════
    // LIST OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * List conversations with optional filtering
     *
     * @param filter - Optional filters
     * @returns Array of conversation metadata
     */
    async listConversations(filter) {
        const index = await this.getIndex();
        let conversations = index.conversations;
        if (filter) {
            if (filter.origin) {
                conversations = conversations.filter(c => c.origin === filter.origin);
            }
            if (filter.documentId) {
                conversations = conversations.filter(c => c.documentId === filter.documentId);
            }
            if (filter.status) {
                conversations = conversations.filter(c => c.status === filter.status);
            }
            if (filter.olderThan) {
                const cutoff = filter.olderThan.toISOString();
                conversations = conversations.filter(c => c.lastActiveAt < cutoff);
            }
            if (filter.newerThan) {
                const cutoff = filter.newerThan.toISOString();
                conversations = conversations.filter(c => c.lastActiveAt > cutoff);
            }
        }
        // Sort by last active (most recent first)
        conversations.sort((a, b) => new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime());
        return conversations;
    }
    /**
     * Get conversations for a specific deck
     */
    async getConversationsForDocument(documentId) {
        return this.listConversations({ documentId });
    }
    /**
     * Get builder conversations (for multi-slide builder)
     */
    async getBuilderConversations() {
        return this.listConversations({ origin: 'builder' });
    }
    /**
     * Get taskpane conversations (main chat)
     */
    async getTaskpaneConversations() {
        return this.listConversations({ origin: 'mainTaskpane' });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // DELETE OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Delete a conversation from disk
     */
    async deleteConversation(conversationId) {
        console.log(`[ConversationPersistence] Deleting conversation: ${conversationId}`);
        try {
            // Delete from backend
            await this.deleteFromBackend(conversationId);
            // Remove from index
            await this.removeFromIndex(conversationId);
            // Mark cache as dirty
            this.indexCacheDirty = true;
            console.log(`[ConversationPersistence] Deleted conversation: ${conversationId}`);
            return true;
        }
        catch (error) {
            console.error(`[ConversationPersistence] Failed to delete conversation:`, error);
            return false;
        }
    }
    /**
     * Delete multiple conversations
     */
    async deleteConversations(conversationIds) {
        let deleted = 0;
        for (const id of conversationIds) {
            if (await this.deleteConversation(id)) {
                deleted++;
            }
        }
        return deleted;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Clean up conversations older than the retention period
     *
     * @param retentionDays - Number of days to keep conversations (1-14)
     * @returns Number of conversations deleted
     */
    async cleanupOldConversations(retentionDays) {
        console.log(`[ConversationPersistence] Cleaning up conversations older than ${retentionDays} days`);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
        const oldConversations = await this.listConversations({ olderThan: cutoffDate });
        if (oldConversations.length === 0) {
            console.log(`[ConversationPersistence] No old conversations to clean up`);
            return 0;
        }
        console.log(`[ConversationPersistence] Found ${oldConversations.length} old conversations to delete`);
        const deleted = await this.deleteConversations(oldConversations.map(c => c.conversationId));
        console.log(`[ConversationPersistence] Cleaned up ${deleted} old conversations`);
        return deleted;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // INDEX MANAGEMENT
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get the conversation index (cached)
     */
    async getIndex() {
        if (this.indexCache && !this.indexCacheDirty) {
            return this.indexCache;
        }
        try {
            const index = await this.loadIndexFromBackend();
            this.indexCache = index;
            this.indexCacheDirty = false;
            return index;
        }
        catch (error) {
            console.warn(`[ConversationPersistence] Failed to load index, creating new one`);
            return {
                version: PERSISTENCE_SCHEMA_VERSION,
                lastUpdated: new Date().toISOString(),
                conversations: [],
            };
        }
    }
    /**
     * Update the index with a conversation's metadata
     */
    async updateIndex(metadata) {
        const index = await this.getIndex();
        // Find existing entry or add new
        const existingIndex = index.conversations.findIndex(c => c.conversationId === metadata.conversationId);
        if (existingIndex >= 0) {
            index.conversations[existingIndex] = metadata;
        }
        else {
            index.conversations.push(metadata);
        }
        index.lastUpdated = new Date().toISOString();
        await this.saveIndexToBackend(index);
    }
    /**
     * Remove a conversation from the index
     */
    async removeFromIndex(conversationId) {
        const index = await this.getIndex();
        index.conversations = index.conversations.filter(c => c.conversationId !== conversationId);
        index.lastUpdated = new Date().toISOString();
        await this.saveIndexToBackend(index);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // BACKEND API CALLS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Save a conversation file via backend API
     *
     * Note: Auth headers are included for consistency and future-proofing,
     * though the local Python backend doesn't currently require authentication.
     */
    async saveToBackend(conversationId, content) {
        const authHeader = authService.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/conversations/${conversationId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
            body: JSON.stringify(content),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to save conversation: ${errorText || response.statusText}`);
        }
    }
    /**
     * Load a conversation file via backend API
     */
    async loadFromBackend(conversationId) {
        const authHeader = authService.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/conversations/${conversationId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
        });
        if (response.status === 404) {
            return null;
        }
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to load conversation: ${errorText || response.statusText}`);
        }
        return response.json();
    }
    /**
     * Delete a conversation file via backend API
     */
    async deleteFromBackend(conversationId) {
        const authHeader = authService.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/conversations/${conversationId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
        });
        if (!response.ok && response.status !== 404) {
            const errorText = await response.text();
            throw new Error(`Failed to delete conversation: ${errorText || response.statusText}`);
        }
    }
    /**
     * Load the index file via backend API
     */
    async loadIndexFromBackend() {
        const authHeader = authService.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/conversations`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to load index: ${errorText || response.statusText}`);
        }
        return response.json();
    }
    /**
     * Save the index file via backend API
     */
    async saveIndexToBackend(index) {
        const authHeader = authService.getAuthHeader();
        const response = await fetch(`${BACKEND_BASE_URL}/conversations/index`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...(authHeader ? { 'Authorization': authHeader } : {}),
            },
            body: JSON.stringify(index),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to save index: ${errorText || response.statusText}`);
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // UTILITY METHODS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Extract metadata from a full conversation
     */
    extractMetadata(conversation) {
        // Count total messages across all threads
        let messageCount = 0;
        for (const thread of Object.values(conversation.threads)) {
            messageCount += thread.messages.length;
        }
        return {
            conversationId: conversation.conversationId,
            origin: conversation.origin,
            title: conversation.title,
            documentId: conversation.documentId,
            planId: conversation.planId,
            createdAt: conversation.createdAt,
            lastActiveAt: conversation.lastActiveAt,
            messageCount,
            status: conversation.status,
            version: conversation.version,
        };
    }
    /**
     * Check if a conversation exists
     */
    async conversationExists(conversationId) {
        const index = await this.getIndex();
        return index.conversations.some(c => c.conversationId === conversationId);
    }
    /**
     * Refresh the index cache from disk
     */
    async refreshIndex() {
        this.indexCacheDirty = true;
        await this.getIndex();
    }
    /**
     * Clear the index cache (for testing)
     */
    clearCache() {
        this.indexCache = null;
        this.indexCacheDirty = true;
    }
    /**
     * Get statistics about persisted conversations
     */
    async getStats() {
        const index = await this.getIndex();
        const byOrigin = {};
        const byStatus = {};
        for (const conv of index.conversations) {
            byOrigin[conv.origin] = (byOrigin[conv.origin] || 0) + 1;
            byStatus[conv.status] = (byStatus[conv.status] || 0) + 1;
        }
        const sorted = [...index.conversations].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        return {
            totalConversations: index.conversations.length,
            byOrigin,
            byStatus,
            oldestConversation: sorted[0]?.conversationId,
            newestConversation: sorted[sorted.length - 1]?.conversationId,
        };
    }
}
// Export singleton getter
export const getConversationPersistence = () => ConversationPersistence.getInstance();
